import { useEffect, useState } from "react";
import { Button, Upload } from "antd";
import { InboxOutlined, CloseOutlined } from "@ant-design/icons";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import useFilePost from "../hooks/useFilePost";
import { openConfirmNotification, openErrorNotification } from "../utils/toast";

const { Dragger } = Upload;

export const ImportFile = ({
  url,
  onSuccess,
  onCancel,
  acceptedFileTypes = ".csv",
  maxFileSize = 10,
  buttonText = "Apply",
  title = "Import File",
}) => {
  const [fileList, setFileList] = useState([]);

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postFile: postAction,
  } = useFilePost(url);

  const onFinish = () => {
    try {
      if (!fileList.length) {
        openErrorNotification("Please select a file to import");
        return;
      }

      const formData = new FormData();
      const file = fileList[0].originFileObj || fileList[0];
      formData.append("file", file);
      postAction(formData);
    } catch (error) {
      console.log(error);
    }
  };

  const onReset = () => {
    setFileList([]);
    onCancel?.();
  };

  const beforeUpload = (file) => {
    // Check file size
    const isLtMaxSize = file.size / 1024 / 1024 < maxFileSize;
    if (!isLtMaxSize) {
      openErrorNotification(`File must be smaller than ${maxFileSize}MB`);
      return Upload.LIST_IGNORE;
    }

    // Check file type
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const acceptedExtensions = acceptedFileTypes
      .split(",")
      .map((ext) => ext.replace(".", "").trim());

    if (!acceptedExtensions.includes(fileExtension)) {
      openErrorNotification(`Only ${acceptedFileTypes} file(s) are allowed`);
      return Upload.LIST_IGNORE;
    }

    setFileList([file]);
    return false; // Prevent auto upload
  };

  const handleRemove = () => {
    setFileList([]);
    return true;
  };

  useEffect(() => {
    if (postError) {
      const errors = postError?.response?.data?.errors;
      if (errors && Array.isArray(errors) && errors.length > 0) {
        // Map over errors and show each on a new line
        const errorMessage = errors.join("\n");
        openErrorNotification(errorMessage);
      } else {
        openErrorNotification("Something went wrong");
      }
    } else if (!postLoading && !postError && postData) {
      openConfirmNotification("Success");
      onSuccess?.(postData);
      setFileList([]);
    }
  }, [postError, postData, postLoading, onSuccess]);

  return (
    <div>
      <Dragger
        beforeUpload={beforeUpload}
        onRemove={handleRemove}
        fileList={fileList}
        maxCount={1}
        accept={acceptedFileTypes}
        disabled={postLoading}
      >
        <p className="ant-upload-drag-icon text-primary">
          <InboxOutlined style={{ color: "var(--text-color)" }} />
        </p>
        <p className="ant-upload-text ">
          Click or drag file to this area to import
        </p>
        <p className="ant-upload-hint">Max size: {maxFileSize}MB</p>
      </Dragger>

      <div className="flex justify-center space-x-2 mt-2.5">
        <Button
          type="primary"
          className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
          onClick={onFinish}
          loading={postLoading}
          disabled={!fileList.length}
        >
          <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
          <span>{buttonText}</span>
        </Button>
        <Button className="reset-button" onClick={onReset}>
          <CloseOutlined />
          <span>Cancel</span>
        </Button>
      </div>
    </div>
  );
};
